import { generateObject } from 'ai'
import { createAnthropic } from '@ai-sdk/anthropic'
import { z } from 'zod'

export const CompletenessResultSchema = z.object({
  score: z.number().min(0).max(1),
  dimension_scores: z.object({
    problem_description: z.number().min(0).max(1),
    affected_equipment: z.number().min(0).max(1),
    observed_symptoms: z.number().min(0).max(1),
    timeline: z.number().min(0).max(1),
    attempted_remedies: z.number().min(0).max(1),
    impact: z.number().min(0).max(1),
  }),
  missing_dimensions: z.array(z.string()),
  questions: z.array(z.string()),
})

export type CompletenessResult = z.infer<typeof CompletenessResultSchema>

export const SummaryResultSchema = z.object({
  title: z.string().min(3).max(140),
  summary: z.string().min(10),
  reasoning_trace: z.string().min(10),
})

export type SummaryResult = z.infer<typeof SummaryResultSchema>

export const ResponseDraftResultSchema = z.object({
  draft: z.string().min(10),
  source_indexes: z.array(z.number().int().min(1)),
  knowledge_gaps: z.array(z.string()),
  reasoning_trace: z.string().min(10),
})

export type ResponseDraftResult = z.infer<typeof ResponseDraftResultSchema>

export const TagSuggestionResultSchema = z.object({
  tags: z.array(z.string().min(1).max(60)),
  reasoning: z.string().min(5),
})

export type TagSuggestionResult = z.infer<typeof TagSuggestionResultSchema>

export const SentimentResultSchema = z.object({
  urgency_score: z.number().min(0).max(1),
  sentiment_score: z.number().min(-1).max(1),
  sentiment_label: z.enum(['frustrated', 'concerned', 'neutral', 'positive', 'urgent', 'distressed']),
  priority_band: z.enum(['low', 'medium', 'high', 'critical']),
  reasoning: z.string().min(10),
})

export type SentimentResult = z.infer<typeof SentimentResultSchema>

export const KbUpdateProposalResultSchema = z.object({
  should_create: z.boolean(),
  title: z.string().min(3).max(140).nullable(),
  slug: z.string().min(3).max(120).nullable(),
  body_markdown: z.string().min(10).nullable(),
  reasoning_trace: z.string().min(10),
})

export type KbUpdateProposalResult = z.infer<typeof KbUpdateProposalResultSchema>

export interface AIGatewayConfig {
  provider: string
  model: string
  temperature: number
  dataProcessingConsent: boolean
}

export class DataProcessingConsentError extends Error {
  constructor() {
    super('LLM call blocked: data_processing_consent is false for this tenant')
    this.name = 'DataProcessingConsentError'
  }
}

/** Calls the LLM to assess completeness. Throws DataProcessingConsentError if consent is off. */
export async function callCompletenessLLM(
  config: AIGatewayConfig,
  system: string,
  user: string,
): Promise<CompletenessResult> {
  if (!config.dataProcessingConsent) throw new DataProcessingConsentError()

  // Only Anthropic implemented for MVP; extend with provider switch for others
  const anthropic = createAnthropic({ apiKey: process.env.ANTHROPIC_API_KEY })
  const model = anthropic(config.model)

  const { object } = await generateObject({
    model,
    temperature: config.temperature,
    schema: CompletenessResultSchema,
    system,
    prompt: user,
  })

  return object
}

/** Calls the LLM to produce an expert-facing ticket summary. */
export async function callSummarizationLLM(
  config: AIGatewayConfig,
  system: string,
  user: string,
): Promise<SummaryResult> {
  if (!config.dataProcessingConsent) throw new DataProcessingConsentError()

  const anthropic = createAnthropic({ apiKey: process.env.ANTHROPIC_API_KEY })
  const model = anthropic(config.model)

  const { object } = await generateObject({
    model,
    temperature: config.temperature,
    schema: SummaryResultSchema,
    system,
    prompt: user,
  })

  return object
}

/** Calls the LLM to draft an expert response grounded in KB sources. */
export async function callResponseDraftLLM(
  config: AIGatewayConfig,
  system: string,
  user: string,
): Promise<ResponseDraftResult> {
  if (!config.dataProcessingConsent) throw new DataProcessingConsentError()

  const anthropic = createAnthropic({ apiKey: process.env.ANTHROPIC_API_KEY })
  const model = anthropic(config.model)

  const { object } = await generateObject({
    model,
    temperature: config.temperature,
    schema: ResponseDraftResultSchema,
    system,
    prompt: user,
  })

  return object
}

/** Calls the LLM to suggest tags from KB vocabulary for a ticket. */
export async function callTagSuggestionLLM(
  config: AIGatewayConfig,
  system: string,
  user: string,
): Promise<TagSuggestionResult> {
  if (!config.dataProcessingConsent) throw new DataProcessingConsentError()

  const anthropic = createAnthropic({ apiKey: process.env.ANTHROPIC_API_KEY })
  const model = anthropic(config.model)

  const { object } = await generateObject({
    model,
    temperature: config.temperature,
    schema: TagSuggestionResultSchema,
    system,
    prompt: user,
  })

  return object
}

/** Calls the LLM to assess urgency, sentiment, and priority for a ticket. */
export async function callSentimentLLM(
  config: AIGatewayConfig,
  system: string,
  user: string,
): Promise<SentimentResult> {
  if (!config.dataProcessingConsent) throw new DataProcessingConsentError()

  const anthropic = createAnthropic({ apiKey: process.env.ANTHROPIC_API_KEY })
  const model = anthropic(config.model)

  const { object } = await generateObject({
    model,
    temperature: config.temperature,
    schema: SentimentResultSchema,
    system,
    prompt: user,
  })

  return object
}

/** Calls the LLM to propose KB updates from an expert response. */
export async function callKbUpdateProposalLLM(
  config: AIGatewayConfig,
  system: string,
  user: string,
): Promise<KbUpdateProposalResult> {
  if (!config.dataProcessingConsent) throw new DataProcessingConsentError()

  const anthropic = createAnthropic({ apiKey: process.env.ANTHROPIC_API_KEY })
  const model = anthropic(config.model)

  const { object } = await generateObject({
    model,
    temperature: config.temperature,
    schema: KbUpdateProposalResultSchema,
    system,
    prompt: user,
  })

  return object
}
